import { useCallback, useEffect, useState } from "react";
import {
  MessageCircle,
  Plus,
  Trash2,
  RefreshCw,
  Send,
  Loader2,
  CheckCircle2,
  XCircle,
  QrCode,
  Unplug,
} from "lucide-react";
import { toast } from "sonner";
import { api, formattaData, dettaglioErrore } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-slate-700 bg-obsidian/60 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-amber-500";

export function WhatsAppPanel() {
  const [statoWeb, setStatoWeb] = useState(null);
  const [qr, setQr] = useState(null);
  const [destinatari, setDestinatari] = useState([]);
  const [log, setLog] = useState([]);
  const [nome, setNome] = useState("");
  const [telefono, setTelefono] = useState("");
  const [invioTel, setInvioTel] = useState("");
  const [invioTesto, setInvioTesto] = useState("");
  const [caricamento, setCaricamento] = useState(false);
  const [invioInCorso, setInvioInCorso] = useState(false);

  const carica = useCallback(async () => {
    try {
      const [s, d, l] = await Promise.all([
        api.get("/admin/wa-web/qr"),
        api.get("/admin/whatsapp/destinatari"),
        api.get("/admin/whatsapp/log"),
      ]);
      setStatoWeb(s.data.stato);
      setQr(s.data.qr);
      setDestinatari(d.data);
      setLog(l.data);
    } catch {
      /* errore silenzioso, riprova al prossimo giro */
    }
  }, []);

  useEffect(() => {
    carica();
    const t = setInterval(carica, 5000);
    return () => clearInterval(t);
  }, [carica]);

  const connesso = statoWeb === "connesso";

  const aggiungi = async (e) => {
    e.preventDefault();
    setCaricamento(true);
    try {
      await api.post("/admin/whatsapp/destinatari", { nome, telefono });
      toast.success("Destinatario aggiunto", {
        description: "Riceverà le nuove richieste su WhatsApp.",
      });
      setNome("");
      setTelefono("");
      carica();
    } catch (err) {
      toast.error("Aggiunta non riuscita", { description: dettaglioErrore(err) });
    } finally {
      setCaricamento(false);
    }
  };

  const toggleAttivo = async (d) => {
    await api.patch(`/admin/whatsapp/destinatari/${d.id}`, { attivo: !d.attivo });
    carica();
  };

  const elimina = async (d) => {
    await api.delete(`/admin/whatsapp/destinatari/${d.id}`);
    toast.success("Destinatario eliminato");
    carica();
  };

  const inviaManuale = async (e) => {
    e.preventDefault();
    setInvioInCorso(true);
    try {
      await api.post("/admin/wa-web/invia", { telefono: invioTel, testo: invioTesto });
      toast.success("Messaggio inviato dal tuo WhatsApp");
      setInvioTesto("");
      carica();
    } catch (err) {
      toast.error("Invio non riuscito", { description: dettaglioErrore(err) });
      carica();
    } finally {
      setInvioInCorso(false);
    }
  };

  const disconnetti = async () => {
    await api.post("/admin/wa-web/disconnetti");
    toast.success("WhatsApp disconnesso");
    carica();
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-800 bg-slatecard p-6" data-testid="whatsapp-stato-card">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2.5 font-display text-lg font-semibold text-white">
              <MessageCircle size={20} className="text-emerald-400" />
              Connessione WhatsApp
            </h3>
            <button
              type="button"
              data-testid="whatsapp-ricontrolla-btn"
              onClick={carica}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:border-amber-500/50 hover:text-amber-400"
            >
              <RefreshCw size={13} /> Ricontrolla
            </button>
          </div>
          <div className="mt-5">
            {statoWeb === null || statoWeb === "avvio" || statoWeb === "riconnessione" ? (
              <p className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 size={15} className="animate-spin" /> Connessione in corso…
              </p>
            ) : connesso ? (
              <div>
                <span
                  data-testid="whatsapp-stato-badge"
                  className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-400"
                >
                  <CheckCircle2 size={16} /> Connesso
                </span>
                <p className="mt-4 text-sm text-slate-300">
                  Il tuo WhatsApp è collegato alla dashboard. Le nuove richieste e gli invii
                  partono direttamente dal tuo numero.
                </p>
                <button
                  type="button"
                  data-testid="whatsapp-disconnetti-btn"
                  onClick={disconnetti}
                  className="mt-5 inline-flex items-center gap-2 rounded-full border border-red-500/40 px-4 py-2 text-xs font-semibold text-red-400 transition-colors hover:bg-red-500/10"
                >
                  <Unplug size={14} /> Disconnetti WhatsApp
                </button>
              </div>
            ) : statoWeb === "in_attesa_qr" && qr ? (
              <div className="text-center">
                <span
                  data-testid="whatsapp-stato-badge"
                  className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm font-semibold text-amber-400"
                >
                  <QrCode size={16} /> In attesa di scansione
                </span>
                <div className="mx-auto mt-5 w-fit rounded-2xl bg-white p-3">
                  <img
                    src={qr}
                    alt="QR code per collegare WhatsApp"
                    data-testid="whatsapp-qr-image"
                    className="h-56 w-56"
                  />
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-400">
                  Apri WhatsApp sul telefono → <span className="text-white">Dispositivi
                  collegati</span> → <span className="text-white">Collega dispositivo</span> e
                  inquadra questo codice.
                </p>
              </div>
            ) : (
              <div>
                <span
                  data-testid="whatsapp-stato-badge"
                  className="inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-400"
                >
                  <XCircle size={16} /> Non connesso
                </span>
                <p className="mt-4 text-sm leading-relaxed text-slate-400">
                  Il codice QR è in preparazione. Se non appare entro pochi secondi, premi
                  "Ricontrolla".
                </p>
              </div>
            )}
          </div>
          <p className="mt-5 rounded-lg bg-obsidian/60 p-4 text-xs leading-relaxed text-slate-500">
            Con la connessione attiva, ogni nuova richiesta che arriva dalla chat o dal modulo
            viene inviata in automatico dal tuo WhatsApp ai numeri attivi qui accanto, e i
            pulsanti "Gira su WhatsApp" inviano direttamente senza aprire altre app. Il
            superadmin vede sempre lo stato della connessione e può intervenire.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slatecard p-6">
          <h3 className="font-display text-lg font-semibold text-white">
            Numeri che ricevono gli interventi
          </h3>
          <form onSubmit={aggiungi} className="mt-5 flex flex-col gap-3 sm:flex-row">
            <input
              data-testid="whatsapp-destinatario-nome-input"
              required
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Nome (es. Marco tecnico)"
              className={inputCls}
            />
            <input
              data-testid="whatsapp-destinatario-telefono-input"
              required
              type="tel"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="+39 333 1234567"
              className={inputCls}
            />
            <button
              type="submit"
              data-testid="whatsapp-aggiungi-destinatario-btn"
              disabled={caricamento}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-obsidian transition-colors hover:bg-amber-400 disabled:opacity-60"
            >
              {caricamento ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              Aggiungi
            </button>
          </form>
          <div className="mt-5 space-y-3">
            {destinatari.length === 0 ? (
              <p className="rounded-xl border border-dashed border-slate-700 p-6 text-center text-sm text-slate-500" data-testid="whatsapp-destinatari-vuoti">
                Nessun numero aggiunto. Aggiungi i tecnici che devono ricevere gli interventi.
              </p>
            ) : (
              destinatari.map((d, i) => (
                <div
                  key={d.id}
                  data-testid={`whatsapp-destinatario-row-${i}`}
                  className="flex items-center justify-between gap-3 rounded-xl border border-slate-800 bg-obsidian/50 px-4 py-3"
                >
                  <div>
                    <p className="text-sm font-semibold text-white">{d.nome}</p>
                    <p className="text-xs text-slate-400">{d.telefono}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid={`whatsapp-destinatario-toggle-${d.id}`}
                      onClick={() => toggleAttivo(d)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                        d.attivo
                          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                          : "border-slate-600 text-slate-500"
                      }`}
                    >
                      {d.attivo ? "Attivo" : "Disattivato"}
                    </button>
                    <button
                      type="button"
                      data-testid={`whatsapp-destinatario-elimina-${d.id}`}
                      onClick={() => elimina(d)}
                      aria-label={`Elimina ${d.nome}`}
                      className="rounded-lg border border-slate-700 p-2 text-slate-400 transition-colors hover:border-red-500/50 hover:text-red-400"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-800 bg-slatecard p-6">
          <h3 className="font-display text-lg font-semibold text-white">Invio manuale</h3>
          <p className="mt-1 text-xs text-slate-500">
            Scrivi un messaggio e invialo dal tuo WhatsApp a qualsiasi numero.
          </p>
          <form onSubmit={inviaManuale} className="mt-5 space-y-3">
            <input
              data-testid="whatsapp-invio-telefono-input"
              required
              type="tel"
              value={invioTel}
              onChange={(e) => setInvioTel(e.target.value)}
              placeholder="+39 333 1234567"
              className={inputCls}
            />
            <textarea
              data-testid="whatsapp-invio-testo-input"
              required
              rows={3}
              value={invioTesto}
              onChange={(e) => setInvioTesto(e.target.value)}
              placeholder="Testo dell'intervento da comunicare…"
              className={`${inputCls} resize-none`}
            />
            <button
              type="submit"
              data-testid="whatsapp-invio-btn"
              disabled={invioInCorso || !connesso}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-6 py-3 text-sm font-bold text-obsidian transition-colors hover:bg-amber-400 disabled:opacity-50"
            >
              {invioInCorso ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Invia dal tuo WhatsApp
            </button>
            {!connesso && (
              <p className="text-xs text-slate-500">
                Disponibile quando WhatsApp è connesso con il QR qui sopra.
              </p>
            )}
          </form>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slatecard p-6">
          <h3 className="font-display text-lg font-semibold text-white">Registro invii</h3>
          <div className="mt-5 max-h-72 space-y-2.5 overflow-y-auto">
            {log.length === 0 ? (
              <p className="rounded-xl border border-dashed border-slate-700 p-6 text-center text-sm text-slate-500" data-testid="whatsapp-log-vuoto">
                Nessun messaggio inviato finora.
              </p>
            ) : (
              log.map((m, i) => (
                <div
                  key={i}
                  data-testid={`whatsapp-log-row-${i}`}
                  className="flex items-start justify-between gap-3 rounded-xl border border-slate-800 bg-obsidian/50 px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm text-slate-200">
                      <span className="font-semibold text-white">{m.nome}</span> · {m.numero}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-slate-500">{m.testo}</p>
                    <p className="mt-1 font-mono text-[10px] text-slate-600">
                      {formattaData(m.created_at)}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${
                      m.esito === "inviato"
                        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                        : "border-red-500/30 bg-red-500/10 text-red-400"
                    }`}
                  >
                    {m.esito === "inviato" ? "Inviato" : "Fallito"}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
